import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { authHref, shortSchoolName, useSchoolBrand } from '../auth/schoolLink';
import { hasPendingGoogleOAuth, signInWithApple, signInWithGoogle } from '../auth/socialSignIn';
import {
  AuthScreen,
  BrandHeader,
  PasswordField,
  SocialButtons,
  errorText,
} from '../components/AuthChrome';
import '../styles/AuthScreen.css';

const Login = ({ onAuthChange }) => {
  const brand = useSchoolBrand();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const finish = async (session) => {
    if (session.created && brand.schoolId) {
      try {
        await apiService.assignSchool(brand.schoolId);
      } catch (assignError) {
        setErrors({ general: errorText(assignError, 'Signed in, but the school could not be saved.') });
        onAuthChange && onAuthChange();
        return;
      }
    }
    onAuthChange && onAuthChange();
    navigate('/');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      const session = await apiService.login(email, password);
      await finish(session);
    } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 401) {
        setErrors({ general: 'Invalid email or password. Please try again.' });
      } else {
        setErrors({ general: errorText(error, 'Login failed. Please try again.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setErrors({});
    try {
      const idToken = await signInWithGoogle();
      const session = await apiService.googleLogin(idToken);
      await finish(session);
    } catch (error) {
      if (!error.cancelled) {
        setErrors({ general: errorText(error, error.message || 'Google sign-in failed.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const googleRef = useRef(handleGoogle);
  googleRef.current = handleGoogle;

  useEffect(() => {
    if (hasPendingGoogleOAuth()) googleRef.current();
  }, []);

  const handleApple = async () => {
    setLoading(true);
    setErrors({});
    try {
      const apple = await signInWithApple();
      const session = await apiService.appleLogin(apple);
      await finish(session);
    } catch (error) {
      if (!error.cancelled) {
        setErrors({ general: errorText(error, error.message || 'Apple sign-in failed.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const teamLine = brand.isSchool
    ? `Log in to earn points for ${shortSchoolName(brand.name) || 'your school'} athletics.`
    : 'Log in to keep earning for your team.';

  return (
    <AuthScreen brand={brand}>
      <BrandHeader brand={brand} logoOnly />
      <h1 className="auth-title is-centered">Welcome back</h1>
      <p className="auth-subtitle is-centered">{teamLine}</p>

      {errors.general ? <div className="auth-alert">{errors.general}</div> : null}

      <SocialButtons onGoogle={handleGoogle} onApple={handleApple} disabled={loading} />

      <div className="auth-divider">or log in with email</div>

      <form onSubmit={handleSubmit}>
        <div className="auth-field">
          <div className="auth-label-row">
            <label className="auth-label" htmlFor="login-email">
              Email address
            </label>
          </div>
          <input
            id="login-email"
            className="auth-input"
            type="email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (errors.email) setErrors({ ...errors, email: '' });
            }}
            placeholder="you@email.com"
            autoComplete="email"
            required
          />
          {errors.email ? <p className="auth-field-error">{errors.email}</p> : null}
        </div>

        <PasswordField
          id="login-password"
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Password"
          autoComplete="current-password"
          labelExtra={
            <Link className="auth-inline-link" to={authHref('/forgot-password', brand.schoolId, { email })}>
              Forgot password?
            </Link>
          }
          error={errors.password}
        />

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading ? 'Logging in…' : 'Log in'}
        </button>
      </form>

      <p className="auth-footer">
        {brand.isSchool ? 'New here? ' : 'New to HomeCrowd? '}
        <Link to={authHref('/signup', brand.schoolId)}>Sign up</Link>
      </p>
    </AuthScreen>
  );
};

export default Login;
